import React from 'react';
import { Button, FileInput, PasswordInput, Select, TextInput } from '@mantine/core';
import { Button as SharedButton } from '../../../../components/Button';
import type { EmployeeView } from '../../../../services/employees/employees.service';
import settingsClasses from './shared/settings-shared.module.scss';

type EmployeeFormState = {
  fullName: string;
  pin: string;
  role: 'manager' | 'employee';
  baseHourlySalary: string;
};

type PosEmployeesSettingsContentProps = {
  form: EmployeeFormState;
  photoFile: File | null;
  editingEmployeeId: number | null;
  settingsEmployees: EmployeeView[];
  isLoadingSettings: boolean;
  isSavingEmployee: boolean;
  attendanceUiEnabled: boolean;
  onChangeForm: (key: keyof EmployeeFormState, value: string) => void;
  onPhotoFileChange: (file: File | null) => void;
  onRefreshEmployees: () => void;
  onResetForm: () => void;
  onSaveEmployee: (event: React.FormEvent<HTMLFormElement>) => void;
  onEditEmployee: (employee: EmployeeView) => void;
  onDeactivateEmployee: (employee: EmployeeView) => void;
  onActivateEmployee: (employee: EmployeeView) => void;
};

const PosEmployeesSettingsContentComponent: React.FC<PosEmployeesSettingsContentProps> = ({
  form,
  photoFile,
  editingEmployeeId,
  settingsEmployees,
  isLoadingSettings,
  isSavingEmployee,
  attendanceUiEnabled,
  onChangeForm,
  onPhotoFileChange,
  onRefreshEmployees,
  onResetForm,
  onSaveEmployee,
  onEditEmployee,
  onDeactivateEmployee,
  onActivateEmployee,
}) => {
  const showSalaryFields = attendanceUiEnabled;

  return (
    <section className={settingsClasses.section}>
      <form onSubmit={onSaveEmployee} className={settingsClasses.section}>
        <div className={settingsClasses.formRow}>
          <TextInput
            data-testid="employees-settings-name"
            label="שם מלא"
            placeholder="שם מלא"
            value={form.fullName}
            onChange={(event) => onChangeForm('fullName', event.currentTarget.value)}
            minLength={2}
            maxLength={120}
            required
            radius="md"
          />
          <PasswordInput
            data-testid="employees-settings-pin"
            label={editingEmployeeId !== null ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'}
            placeholder={editingEmployeeId !== null ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'}
            value={form.pin}
            onChange={(event) => onChangeForm('pin', event.currentTarget.value)}
            minLength={editingEmployeeId !== null ? undefined : 4}
            maxLength={10}
            required={editingEmployeeId === null}
            radius="md"
          />
          <Select
            data-testid="employees-settings-role"
            label="תפקיד"
            data={[
              { value: 'employee', label: 'עובד' },
              { value: 'manager', label: 'מנהל' },
            ]}
            value={form.role}
            onChange={(value) => onChangeForm('role', (value ?? 'employee') as 'manager' | 'employee')}
            radius="md"
          />
          {showSalaryFields && (
            <TextInput
              data-testid="employees-settings-hourly-salary"
              label="שכר שעתי"
              placeholder="לדוגמה: 45"
              value={form.baseHourlySalary}
              onChange={(event) => onChangeForm('baseHourlySalary', event.currentTarget.value)}
              inputMode="decimal"
              radius="md"
            />
          )}
        </div>
        <div className={settingsClasses.fileField}>
          <label className={settingsClasses.fileFieldLabel}>תמונת עובד</label>
          <FileInput
            data-testid="employees-settings-photo"
            accept="image/jpeg,image/webp"
            onChange={onPhotoFileChange}
            placeholder="בחר קובץ"
            aria-label="Employee Photo"
            radius="md"
            clearable
          />
          {photoFile && <span className={settingsClasses.lead}>תמונה נבחרה: {photoFile.name}</span>}
        </div>
        <div className={settingsClasses.formActions}>
          <Button type="button" variant="default" radius="md" data-testid="employees-settings-refresh" onClick={onRefreshEmployees} disabled={isLoadingSettings}>
            {isLoadingSettings ? 'מרענן...' : 'רענן'}
          </Button>
          {editingEmployeeId !== null && (
            <Button type="button" variant="default" radius="md" data-testid="employees-settings-cancel-edit" onClick={onResetForm}>
              בטל עריכה
            </Button>
          )}
          <SharedButton type="submit" variant="save" size="sm" disabled={isSavingEmployee} dataTestId="employees-settings-submit">
            {isSavingEmployee ? 'שומר...' : editingEmployeeId !== null ? 'שמור עובד' : 'צור עובד'}
          </SharedButton>
        </div>
      </form>

      <div className={settingsClasses.tableWrap}>
        <table className={settingsClasses.table}>
          <thead>
            <tr>
              <th>תמונה</th>
              <th>שם מלא</th>
              <th>תפקיד</th>
              {showSalaryFields && <th>שכר שעתי</th>}
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {settingsEmployees.map((employee) => (
              <tr key={employee.id}>
                <td>
                  {employee.photoUrl ? (
                    <img src={employee.photoUrl} alt={`${employee.fullName} profile`} className={settingsClasses.thumb} />
                  ) : (
                    <span className={settingsClasses.thumbPlaceholder}>—</span>
                  )}
                </td>
                <td>{employee.fullName}</td>
                <td>{employee.role === 'manager' ? 'מנהל' : 'עובד'}</td>
                {showSalaryFields && <td>{employee.baseHourlySalary == null ? '—' : `${employee.baseHourlySalary}`}</td>}
                <td>
                  <span className={employee.isActive ? settingsClasses.statusActive : settingsClasses.statusInactive}>
                    {employee.isActive ? 'פעיל' : 'לא פעיל'}
                  </span>
                </td>
                <td>
                  <div className={settingsClasses.actionsCell}>
                    <Button size="xs" variant="default" radius="md" data-testid={`employees-settings-edit-${employee.id}`} onClick={() => onEditEmployee(employee)}>
                      ערוך
                    </Button>
                    {employee.isActive ? (
                      <Button size="xs" color="red" radius="md" data-testid={`employees-settings-toggle-${employee.id}`} onClick={() => onDeactivateEmployee(employee)}>
                        השבת
                      </Button>
                    ) : (
                      <Button size="xs" color="green" radius="md" data-testid={`employees-settings-toggle-${employee.id}`} onClick={() => onActivateEmployee(employee)}>
                        הפעל
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {settingsEmployees.length === 0 && !isLoadingSettings && (
              <tr>
                <td colSpan={showSalaryFields ? 6 : 5} className={settingsClasses.empty}>אין עובדים לטננט זה עדיין.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const PosEmployeesSettingsContent = React.memo(PosEmployeesSettingsContentComponent);
